import React, { useState, useEffect } from 'react'
import { useTranslation } from '~/hooks/useTranslation'
import { getApi } from '~/util/api'
import { Button, Card, Typography } from '~/components/_atoms'
import { Stack } from '~/components/_layouts'
import Spinner from '~/components/shared/Spinner'
import YnsIcon from '~/components/icons/YnsIcon'
import { SERVICE_REQUEST_ICONS } from '~/components/coach_services/ServiceRequestIcons'
import moment from 'moment'
import _ from 'lodash'

export function CoachMeetingsCard({ className, color = 'primary' }) {
  const [isLoading, setIsLoading] = useState(false)
  const [calendlyEvents, setCalendlyEvents] = useState(null)
  const { t } = useTranslation()

  useEffect(() => {
    const fetchMeetings = async () => {
      setIsLoading(true)
      try {
        const response = await getApi().get(
          '/coach_services/check_calendly_events',
        )
        if (response.status === 200) {
          const data = response.data
          // Parse the service_requests JSON string
          const serviceRequests = JSON.parse(data.service_requests)
          console.log('serviceRequests', serviceRequests)
          // Extract calendly events and service request types from each service request
          const events = Object.entries(serviceRequests)
            .filter(([_type, request]) => {
              // console.log('request', type, _.isEmpty(request.calendly_event))
              return !_.isEmpty(request.calendly_event)
            })
            .map(([type, request]) => ({
              ...request.calendly_event,
              serviceRequestType: type,
            }))
            .filter((event) =>
              moment(event.start_time).isSameOrAfter(moment().startOf('day')),
            )
            .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
          console.log('events', events)
          setCalendlyEvents(events)
        } else {
          throw new Error('Failed to fetch next meeting')
        }
      } catch (error) {
        console.error('Failed to fetch next meeting:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchMeetings()
  }, [])

  return (
    <Card
      className={className}
      color={color}
      title={`${t('coach_meetings_card.title')}${
        calendlyEvents?.length > 0 ? ` (${calendlyEvents.length})` : ''
      }`}
      titleColor="secondary"
      icon={<YnsIcon name="calendar" size={24} />}
      actions={
        <Button
          href="/coach_services/professional_services"
          variant="outline"
          color="secondary"
          fullWidth
          className="ga-trackable"
        >
          {t('coach_meetings_card.view_action')}
        </Button>
      }
    >
      <Stack spacing={2}>
        {isLoading ? (
          <Spinner show size={9} />
        ) : calendlyEvents && !_.isEmpty(calendlyEvents) ? (
          <>
            <Typography variant="body2">
              {t('coach_meetings_card.description')}
            </Typography>
            <div className="grid gap-4">
              {calendlyEvents.slice(0, 2).map((event) => (
                <div
                  key={event.id}
                  className="grid grid-cols-[auto_1fr_auto] items-center gap-4"
                >
                  <YnsIcon
                    name={
                      SERVICE_REQUEST_ICONS[event.serviceRequestType] || 'call'
                    }
                    className="text-cod-gray-800"
                    size={24}
                  />
                  <Stack spacing={1}>
                    <Typography variant="body1" className="font-bold">
                      {event.name}
                    </Typography>
                    <Typography variant="body2" className="text-gray-600">
                      {moment(event.start_time).format('MM/DD/YYYY')}
                      {' • '}
                      {event.formatted_start_at}
                    </Typography>
                  </Stack>
                  <Button
                    href={event.join_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="ghost"
                    color="secondary"
                    size="small"
                  >
                    {t('coach_meetings_card.join_meeting')}
                  </Button>
                </div>
              ))}
            </div>
          </>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2">
              {t('coach_meetings_card.descriptionEmpty')}
            </Typography>
            <div className="flex flex-col items-center justify-center gap-2">
              <img
                src="https://www-yns-com.s3.us-east-2.amazonaws.com/images/coach_services/empty_meetings.png"
                alt={t('coach_meetings_card.no_meetings')}
              />
              <Typography variant="body2">
                {t('coach_meetings_card.no_meetings')}
              </Typography>
              <a href="/coach_services/professional_services">
                <Typography variant="small">
                  {t('coach_meetings_card.get_started')}
                </Typography>
              </a>
            </div>
          </Stack>
        )}
      </Stack>
    </Card>
  )
}
